import { useMutation } from 'react-apollo'

import { UPDATE_FULFILLMENT_AND_CLEAR_CART } from '../../apollo/cart/cart.graphql'
import { isISODateTimeEqual } from '../../utils/time-utils'
import { useCart } from '../CartProvider/CartProvider'
import { dataByTypename } from '../../utils/apollo-helpers'
import { normalizeFulfillment } from '../FulfillmentSelectorModal/fulfillment-helpers'
import { useCallback, useReducer } from 'react'
import isEqual from 'lodash/isEqual'

// this is necessary because times locally versus times from bff
// differ slightly, so the times need to be compared in a normalized fashion
const needsUpdate = (a, b) => {
  a = normalizeFulfillment(a)
  b = normalizeFulfillment(b)
  return a.fulfillmentType !== b.fulfillmentType ||
    a.diningOptionBehavior !== b.diningOptionBehavior ||
    !isEqual(a.deliveryInfo, b.deliveryInfo) ||
    !isISODateTimeEqual(a.fulfillmentTime, b.fulfillmentTime)
}

export const useUpdateFulfillment = () => {
  const [updateFulfillmentAndClearCart] = useMutation(
    UPDATE_FULFILLMENT_AND_CLEAR_CART
  )
  const { cartGuid } = useCart()
  const [fulfillmentState, setFulfillmentState] = useReducer(
    (_, { data, error, loading }) => ({ data, error, loading }),
    { loading: false }
  )

  const mutation = useCallback(
    async (prev, next) => {
      if (needsUpdate(prev, next)) {
        setFulfillmentState({
          ...prev,
          loading: true
        })

        try {
          const response = await updateFulfillmentAndClearCart({
            variables: {
              input: {
                cartGuid,
                cartFulfillmentInput: {
                  diningOptionBehavior: next.diningOptionBehavior,
                  fulfillmentType: next.fulfillmentType,
                  fulfillmentDateTime: next.fulfillmentTime,
                  deliveryInfo: next.deliveryInfo
                }
              }
            }
          })
          const { CartModificationError, CartResponse } = dataByTypename(
            response.data.updateFulfillmentAndClearCart
          )
          if (CartModificationError) {
            const { message } = CartModificationError
            const error = new Error(message)
            setFulfillmentState({ error, loading: false })
            return { error }
          }

          const { cart } = CartResponse
          const data = normalizeFulfillment(cart)
          setFulfillmentState({ data, loading: false })
          return { data }
        } catch (error) {
          setFulfillmentState({ error, loading: false })
          return { error }
        }
      }
      setFulfillmentState({ data: next, loading: false })
      return { data: next }
    },
    [cartGuid, setFulfillmentState, updateFulfillmentAndClearCart]
  )

  return [mutation, fulfillmentState]
}
